// Note @neoxu(10/23/2025): This is the old notification component. It will be removed once the notification tray v2 is fully released.
import {
  Grid,
  Typography,
  Badge,
  Card,
  CardActionArea,
  Link,
  NotificationsIcon,
  Avatar,
} from '@rbx/ui';
import React, { FunctionComponent, useCallback, useMemo } from 'react';
import { CreatorStreamNotification } from '@rbx/clients/creatorNotificationStreamsApi';
import { Thumbnail2d, ThumbnailTypes } from '@rbx/thumbnails';
import { useTranslation } from '@rbx/intl';
import Dompurify from 'dompurify';
import useNotificationStyles from './Notification.styles';
import useNavigationConfigs from '../../hooks/useNavigationConfigs';
import { clickNotificationEventModel } from '../../event/eventConstants';
import useElapsedTime from '../utils/useElapsedTime';
import NotificationTargetType from '../utils/NotificationTargetType';
import NOTIFICATION_ICON_MAP from '../utils/notificationIconMap';

type TNotificationProps = {
  notificationContent: CreatorStreamNotification;
  markRead?: (index: number) => void;
  notificationGroupIndex?: number;
};

const NotificationLinkWrapper: FunctionComponent<{
  notification: CreatorStreamNotification;
  onNotificationClick: () => void;
  children?: React.ReactNode;
}> = ({ notification, onNotificationClick, children }) => {
  if (notification?.creatorStreamNotificationContent?.clickAction) {
    return (
      <CardActionArea onClick={onNotificationClick}>
        <Link
          href={notification?.creatorStreamNotificationContent?.clickAction}
          color='inherit'
          target='_blank'
          underline='none'>
          {children}
        </Link>
      </CardActionArea>
    );
  }

  return <React.Fragment>{children}</React.Fragment>;
};

const Notification: FunctionComponent<TNotificationProps> = ({
  notificationContent,
  markRead,
  notificationGroupIndex,
}) => {
  const { translate } = useTranslation();
  const { sendEvent } = useNavigationConfigs();
  const { classes: styles, cx } = useNotificationStyles();
  const onNotificationClick = useCallback(() => {
    sendEvent(clickNotificationEventModel(notificationContent, notificationGroupIndex));
  }, [notificationContent, sendEvent, notificationGroupIndex]);
  const elapsedTime = useElapsedTime((notificationContent.createdUtcTimeInMs ?? 0) / 1000);
  const NotificationIcon = useMemo(() => {
    const targetId = notificationContent?.creatorStreamNotificationContent?.targetId;
    switch (notificationContent?.creatorStreamNotificationContent?.targetType) {
      case NotificationTargetType.Universe:
        return (
          <Avatar
            alt={translate('Label.PlaceIconAltString')}
            variant='rounded'
            className={cx(styles.thumbnailContainer)}>
            <Thumbnail2d
              targetId={Number(targetId) ?? 0}
              type={ThumbnailTypes.gameIcon}
              alt={translate('Label.PlaceIconAltString')}
            />
          </Avatar>
        );
      case NotificationTargetType.User:
        return (
          <Avatar
            alt={translate('Label.UserIconAltString')}
            variant='rounded'
            className={cx(styles.thumbnailContainer)}>
            <Thumbnail2d
              targetId={Number(targetId) ?? 0}
              type={ThumbnailTypes.avatarHeadshot}
              alt={translate('Label.UserIconAltString')}
            />
          </Avatar>
        );
      case NotificationTargetType.Static: {
        const staticIcon =
          NOTIFICATION_ICON_MAP[notificationContent?.creatorStreamNotificationContent?.targetId];
        if (staticIcon) {
          return staticIcon;
        }

        return <NotificationsIcon color='action' />;
      }
      default:
        return <NotificationsIcon color='action' />;
    }
  }, [
    cx,
    notificationContent?.creatorStreamNotificationContent?.targetId,
    notificationContent?.creatorStreamNotificationContent?.targetType,
    styles.thumbnailContainer,
    translate,
  ]);

  const sanitizedNotificationTitle = useMemo(() => {
    return Dompurify.sanitize(`${notificationContent?.creatorStreamNotificationContent?.title}`, {
      ALLOWED_TAGS: ['b', 'i', 'u', 's', 'br'],
      ALLOWED_ATTR: [],
    });
  }, [notificationContent?.creatorStreamNotificationContent?.title]);

  return (
    <Card classes={{ root: styles.cardRadius }}>
      <NotificationLinkWrapper
        notification={notificationContent}
        onNotificationClick={onNotificationClick}>
        <Grid
          className={styles.container}
          container
          onClick={() => {
            if (markRead && notificationGroupIndex !== undefined && !notificationContent.read) {
              markRead(notificationGroupIndex);
            }
          }}>
          <Grid item XSmall='auto'>
            {NotificationIcon}
          </Grid>
          <Grid item XSmall className={styles.notificationText} zeroMinWidth>
            <Grid container direction='column'>
              <Typography
                variant='body1'
                className={styles.truncatedText}
                dangerouslySetInnerHTML={{
                  __html: sanitizedNotificationTitle,
                }}
              />
              {notificationContent?.creatorStreamNotificationContent?.body && (
                <Typography variant='body2' color='secondary' className={styles.truncatedText}>
                  {notificationContent.creatorStreamNotificationContent.body}
                </Typography>
              )}
              <Typography variant='footer' color='secondary'>
                {elapsedTime}
              </Typography>
            </Grid>
          </Grid>
          <Grid item XSmall='auto'>
            <div className={styles.readIconContainer}>
              {!notificationContent.read &&
                notificationContent?.creatorStreamNotificationContent?.clickAction && (
                  <Badge variant='dot' />
                )}
              {!notificationContent?.creatorStreamNotificationContent?.clickAction && (
                <Badge variant='dot' classes={{ badge: styles.disabledDot }} />
              )}
            </div>
          </Grid>
        </Grid>
      </NotificationLinkWrapper>
    </Card>
  );
};

export default Notification;
